import React from 'react';
import { WarningBar } from '@/shared/components/ui';
import { useDisconnectedChannels } from '@/shared/hooks/useDisconnectedChannels';
import { usePermissions } from '@/hooks/usePermissions';

/**
 * Barra de aviso quando um ou mais canais estão desconectados.
 * Renderizada NO FLUXO (topo da coluna de conteúdo, acima do <main>) —
 * nunca como overlay flutuante, para não sobrepor toolbars/busca (R0.1).
 * Anatomia vive no primitivo shared `WarningBar`.
 *
 * @example
 * <DisconnectedChannelWidget />
 */
export const DisconnectedChannelWidget: React.FC = () => {
  const { disconnectedChannels, hasDisconnectedChannels, isLoading } = useDisconnectedChannels();
  const [isDismissed, setIsDismissed] = React.useState(false);
  const { canAccessPath } = usePermissions();

  // Reset dismissed state when disconnected channels change
  React.useEffect(() => {
    if (disconnectedChannels.length > 0) {
      setIsDismissed(false);
    }
  }, [disconnectedChannels.length]);

  // Don't show if loading, no disconnected channels, or dismissed
  if (isLoading || !hasDisconnectedChannels || isDismissed) {
    return null;
  }

  const channelCount = disconnectedChannels.length;
  const channelNames = disconnectedChannels.map((c) => c.name).join(', ');

  return (
    <WarningBar
      data-testid="disconnected-channel-banner"
      truncate
      message={
        channelCount === 1 ? (
          <>
            O canal <strong>{channelNames}</strong> está desconectado.
          </>
        ) : (
          <>
            <strong>{channelCount} canais</strong> estão desconectados.
          </>
        )
      }
      // "Verificar" leva à tela de Canais — só para quem pode abri-la. O aviso
      // em si continua para todos: quem atende precisa saber que o canal caiu.
      action={canAccessPath('/settings/channels') ? { label: 'Verificar', to: '/settings/channels' } : undefined}
      onDismiss={() => setIsDismissed(true)}
      dismissTestId="disconnected-channel-dismiss"
    />
  );
};
